import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SearchBar } from '../UncommittedTracker/SearchBar';
import { StatisticsPlayerTable } from './StatisticsPlayerTable';
import { CoachSidebar } from '../../../components/coach/dashboard/CoachSidebar';
import { SimpleDropdown } from '../../../components/common/SimpleDropdown';
import { fetchUncommittedPlayers } from '../../../features/coach/coachProfileSlice';
import { StatsFilterModal } from '../../../components/coach/dashboard/StatsFilterModal';
import { PlayerFilterModal } from '../../../components/common/PlayerFilterModal';
import { DateRangePicker } from '../../../components/common/DateRangePicker';
import { FiSliders } from "react-icons/fi";
import { statsSeasonYear } from '../../../utils/season';

function MainStatisticsScreen() {
  const { activeView } = useOutletContext();
  const dispatch = useDispatch();
  const { uncommittedPlayers, uncommittedLoading, uncommittedError, uncommittedPagination } = useSelector((state) => state.coachProfile);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statCategory, setStatCategory] = useState('HITTING'); // '', HITTING, PITCHING
  const [uncommittedOnly, setUncommittedOnly] = useState('uncommitted'); // Default to uncommitted
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedStatColumns, setSelectedStatColumns] = useState([]);
  const [filterActiveTab, setFilterActiveTab] = useState(null);
  const [filterRanges, setFilterRanges] = useState({}); // Store min-max filter values
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [limit, setLimit] = useState(20);
  const filterButtonRef = useRef(null);
  const [position, setPosition] = useState('');
  const [showPlayerFilterModal, setShowPlayerFilterModal] = useState(false);
  const [playerFilters, setPlayerFilters] = useState({});
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [headerNameSearch, setHeaderNameSearch] = useState('');
  const [headerSchoolSearch, setHeaderSchoolSearch] = useState('');
  const [qualifiedPlayer, setQualifiedPlayer] = useState(false);

  // Position options
  // const positionOptions = [
  //   { label: 'All', value: '' },
  //   { label: 'Pitcher', value: 'P' },
  //   { label: 'C', value: 'C' },
  //   { label: '1B', value: '1B' },
  //   { label: '2B', value: '2B' },
  //   { label: '3B', value: '3B' },
  //   { label: 'SS', value: 'SS' },
  //   { label: 'LF', value: 'LF' },
  //   { label: 'CF', value: 'CF' },
  //   { label: 'RF', value: 'RF' },
  //   { label: 'INF', value: 'INF' },
  //   { label: 'OF', value: 'OF' },
  // ];

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch players on mount and when filters change
  // Send statCategory to backend so it filters by position server-side before sorting/paginating
  useEffect(() => {
    const params = {
      page: 1,
      limit,
      seasonYear: statsSeasonYear(),
    };

    // Add stat category so backend filters by position (pitchers vs hitters)
    if (statCategory) {
      params.category = statCategory;
    }

    // Add statsType (batting / pitching / fielding) based on current tab
    {
      const statsTypeMap = { 'HITTING': 'batting', 'PITCHING': 'pitching', 'FIELDING': 'fielding', 'BASE RUNNING': 'batting' };
      const tabMap = { 'hitting': 'batting', 'pitching': 'pitching', 'fielding': 'fielding', 'base running': 'batting' };
      const resolved = statsTypeMap[statCategory] || tabMap[(filterActiveTab || '').toLowerCase()];
      if (resolved) params.statsType = resolved;
    }

    // Add position filter
    if (position && position !== 'All') {
      params.position = position;
    }

    // Add search name
    if (debouncedSearch) {
      params.name = debouncedSearch;
    }

    // Add commitmentStatus based on dropdown value
    if (uncommittedOnly) {
      params.commitmentStatus = uncommittedOnly;
    }

    // Add filter ranges for API filtering
    if (Object.keys(filterRanges).length > 0) {
      params.filters = filterRanges;
    }

    // Add sort params
    if (sortBy) {
      params.sortBy = sortBy;
      params.sortOrder = sortOrder;
    }

    // Add player filter modal params
    if (playerFilters.conference) params.conference = playerFilters.conference;
    if (playerFilters.division) params.division = playerFilters.division;
    if (playerFilters.classification) params.classYear = playerFilters.classification;
    if (playerFilters.position && !position) params.position = playerFilters.position;
    if (playerFilters.playerName && !debouncedSearch) params.name = playerFilters.playerName;
    if (playerFilters.teamId) params.teamId = playerFilters.teamId;
    if (playerFilters.region) params.region = playerFilters.region;
    if (playerFilters.state) params.state = playerFilters.state;
    if (playerFilters.handedness) params.handedness = playerFilters.handedness;
    if (playerFilters.splits) params.splits = playerFilters.splits;

    // Add multi-select position filter from table header
    if (selectedPositions.length > 0) params.position = selectedPositions.join(',');
    if (headerNameSearch) params.name = headerNameSearch;
    if (headerSchoolSearch) params.schoolName = headerSchoolSearch;

    // Add date range params
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    // Add qualified player filter
    params.qualifiedPlayer = qualifiedPlayer;

    dispatch(fetchUncommittedPlayers(params));
  }, [dispatch, position, debouncedSearch, uncommittedOnly, filterRanges, sortBy, sortOrder, limit, playerFilters, dateFrom, dateTo, statCategory, selectedPositions, headerNameSearch, headerSchoolSearch, qualifiedPlayer]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > uncommittedPagination.totalPages) return;

    const params = {
      page: newPage,
      limit,
      seasonYear: statsSeasonYear(),
    };

    // Add stat category so backend filters by position
    if (statCategory) {
      params.category = statCategory;
    }

    // Add position filter
    if (position && position !== 'All') {
      params.position = position;
    }

    // Add search name
    if (debouncedSearch) {
      params.name = debouncedSearch;
    }

    // Add commitmentStatus based on dropdown value
    if (uncommittedOnly) {
      params.commitmentStatus = uncommittedOnly;
    }

    // Add filter ranges for API filtering
    if (Object.keys(filterRanges).length > 0) {
      params.filters = filterRanges;
    }

    // Add sort params
    if (sortBy) {
      params.sortBy = sortBy;
      params.sortOrder = sortOrder;
    }

    // Add player filter modal params
    if (playerFilters.conference) params.conference = playerFilters.conference;
    if (playerFilters.division) params.division = playerFilters.division;
    if (playerFilters.classification) params.classYear = playerFilters.classification;
    if (playerFilters.position && !position) params.position = playerFilters.position;
    if (playerFilters.playerName && !debouncedSearch) params.name = playerFilters.playerName;
    if (playerFilters.teamId) params.teamId = playerFilters.teamId;
    if (playerFilters.region) params.region = playerFilters.region;
    if (playerFilters.state) params.state = playerFilters.state;
    if (playerFilters.handedness) params.handedness = playerFilters.handedness;
    if (playerFilters.splits) params.splits = playerFilters.splits;

    // Add multi-select position filter from table header
    if (selectedPositions.length > 0) params.position = selectedPositions.join(',');
    if (headerNameSearch) params.name = headerNameSearch;
    if (headerSchoolSearch) params.schoolName = headerSchoolSearch;

    // Add date range params
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    // Add qualified player filter
    params.qualifiedPlayer = qualifiedPlayer;

    dispatch(fetchUncommittedPlayers(params));

    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleApplyFilters = (filters) => {
    console.log('Applied filters:', filters);

    // Store selected filters for dynamic column display
    if (filters.selectedFilters && filters.selectedFilters.length > 0) {
      setSelectedStatColumns(filters.selectedFilters);
      setFilterActiveTab(filters.activeTab);
    } else {
      setSelectedStatColumns([]);
      setFilterActiveTab(null);
    }

    // Store filter ranges for API filtering (triggers refetch via useEffect)
    if (filters.filterRanges) {
      setFilterRanges(filters.filterRanges);
    } else {
      setFilterRanges({});
    }
  };

  const handleSort = (backendField, direction) => {
    setSortBy(backendField);
    setSortOrder(direction);
  };

  const handleResetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setDateFrom('');
    setDateTo('');
    setStatCategory('HITTING');
    setUncommittedOnly('uncommitted');
    setSelectedStatColumns([]);
    setFilterActiveTab(null);
    setFilterRanges({});
    setSortBy(null);
    setSortOrder('asc');
    setPosition('');
    setPlayerFilters({});
    setSelectedPositions([]);
    setHeaderNameSearch('');
    setHeaderSchoolSearch('');
    setQualifiedPlayer(false);
  };


  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Left Sidebar */}
      <div className="lg:col-span-1 ">
        <CoachSidebar activeView={activeView} />
      </div>

      {/* Right Content Area */}
      <div className="lg:col-span-3 w-full mx-auto bg-jp-card border border-jp-line rounded-3xl shadow-sm px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 ">
      {/* Header Section */}
      <div className="mb-4 text-left">
        <div className="flex items-center gap-4 mb-3">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-jp-ink">
            Statistics
          </h1>
          <button
            type="button"
            onClick={() => setQualifiedPlayer(!qualifiedPlayer)}
            style={{ marginLeft: 'auto' }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
              qualifiedPlayer
                ? 'bg-cyan-500 border-cyan-500 text-white'
                : 'bg-transparent border-jp-line text-jp-muted hover:border-jp-accent/50 hover:text-jp-ink'
            }`}
          >
            <span
              className={`relative inline-flex w-9 h-5 rounded-full transition-colors ${
                qualifiedPlayer ? 'bg-cyan-300' : 'bg-jp-section'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  qualifiedPlayer ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </span>
            Qualified Players
          </button>
        </div>

        {/* Search and Filters Row */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-end gap-3">
          {/* Filters Section */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-wrap">
            {/* Name Search Bar */}
            <div className="w-full sm:w-auto" style={{ minWidth: '250px', maxWidth: '400px' }}>
              <SearchBar value={search} onChange={setSearch} />
            </div>

            {/* Commitment Status Dropdown */}
            <div className="w-full sm:w-auto" style={{ minWidth: '180px' }}>
              <SimpleDropdown
                value={uncommittedOnly}
                onChange={(val) => setUncommittedOnly(val)}
                options={[
                  { label: 'All Players', value: '' },
                  { label: 'Committed', value: 'committed' },
                  { label: 'Uncommitted', value: 'uncommitted' },
                ]}
                placeholder="Commitment Status"
                variant="form"
              />
            </div>

            {/* Category Dropdown */}
            <div className="w-full sm:w-auto" style={{ minWidth: '160px' }}>
              <SimpleDropdown
                value={statCategory}
                onChange={(val) => setStatCategory(val)}
                options={[
                  { value: '', label: 'HITTING/PITCHING' },
                  { value: 'HITTING', label: 'HITTING' },
                  { value: 'PITCHING', label: 'PITCHING' },
                  // { value: 'BASE RUNNING', label: 'BASE RUNNING' },
                  // { value: 'FIELDING', label: 'FIELDING' },
                ]}
                placeholder="HITTING/PITCHING"
                variant="form"
              />
            </div>

            {/* Position Dropdown — commented out, moved to Filter modal */}
            {/* <div className="w-full sm:w-auto" style={{ minWidth: '180px' }}>
              <SimpleDropdown value={position} onChange={(val) => setPosition(val)} options={positionOptions} placeholder="POSITION" variant="form" />
            </div> */}

            {/* Date Range - temporarily disabled */}
            {/* <DateRangePicker
              startDate={dateFrom}
              endDate={dateTo}
              onChange={({ startDate, endDate }) => { setDateFrom(startDate); setDateTo(endDate); }}
              placeholder="Select date range"
            /> */}

            {/* Filters button */}
            <button
              onClick={() => setShowPlayerFilterModal(true)}
              className="inline-flex items-center justify-center gap-2 text-jp-ink font-medium h-11 px-4
                         rounded-lg border border-jp-line hover:bg-jp-section transition-colors
                         w-full sm:w-auto text-sm whitespace-nowrap"
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-jp-ink">
                <path d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A.998.998 0 0 0 18.95 4H5.04a1 1 0 0 0-.79 1.61z"/>
              </svg>
              <span>Filters</span>
              {Object.values(playerFilters).filter(Boolean).length > 0 && (
                <span className="bg-cyan-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {Object.values(playerFilters).filter(Boolean).length}
                </span>
              )}
            </button>

            {/* Add/Subtract Stats Filters button */}
            <div className="relative w-full sm:w-auto">
              <button
                ref={filterButtonRef}
                onClick={() => setShowFilterModal(!showFilterModal)}
                className="inline-flex items-center justify-center gap-2 text-jp-ink font-medium h-11 px-4
                           rounded-lg border border-jp-line hover:bg-jp-section transition-colors
                           w-full sm:w-auto text-sm whitespace-nowrap"
                type="button"
              >
                <span>Add/Subtract Stats Filters</span>
                <FiSliders className="text-jp-ink" size={18} />
              </button>

              {/* Stats Filter Modal */}
              <StatsFilterModal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                onApply={handleApplyFilters}
                buttonRef={filterButtonRef}
                defaultSelectedFilters={statCategory === 'PITCHING'
                  ? ['ip', 'gs', 'era', 'whip', 'so', 'bb', 'k%', 'bb%']
                  : ['ab', 'avg', 'obp', 'slg', 'runs', 'h', '2b', '3b', 'hr', 'k', 'bb']}
                defaultActiveTab={statCategory === 'PITCHING' ? 'pitching' : 'hitting'}
              />
            </div>

            {/* Player Filter Modal */}
            <PlayerFilterModal
              isOpen={showPlayerFilterModal}
              onClose={() => setShowPlayerFilterModal(false)}
              onApply={(f) => setPlayerFilters(f)}
              initialFilters={playerFilters}
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="rounded-lg bg-jp-card overflow-hidden">
        {/* Player Table with horizontal scroll */}
        <div className="overflow-x-auto">
          <StatisticsPlayerTable
            data={uncommittedError ? [] : uncommittedPlayers}
            loading={uncommittedLoading}
            error={uncommittedError}
            statCategory={statCategory}
            pagination={uncommittedPagination}
            onPageChange={handlePageChange}
            limit={limit}
            onLimitChange={(val) => setLimit(val)}
            selectedStatColumns={selectedStatColumns}
            filterActiveTab={filterActiveTab}
            sortColumn={sortBy}
            sortDirection={sortOrder}
            onSort={handleSort}
            selectedPositions={selectedPositions}
            onPositionFilter={setSelectedPositions}
            nameSearch={headerNameSearch}
            onNameSearch={setHeaderNameSearch}
            schoolSearch={headerSchoolSearch}
            onSchoolSearch={setHeaderSchoolSearch}
            onResetFilters={handleResetFilters}
          />
        </div>
      </div>
      </div>
    </div>
  )
}

export default MainStatisticsScreen